import { useEffect, useState } from "react"
import * as Sentry from "@sentry/react"
import ModalCustom from "components/ModalCustom"
import Button from "components/Button/Button"
import { useBoolean } from "helpers/hooks"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { pushTo } from "helpers/history"
import { PATH } from "constants/path"
import { getEncryptedKeyMiddleware } from "pages/auth/services/api"
import { updateData, getStoreData, Stores } from "services/indexedDB"
import { generateTRCCode } from "services/keyHealthService"
import { EncryptionKeys } from "controllers/EncryptionController"
import { GetKeysType } from "types"
import { ReactComponent as IconHasKey } from "assets/images/icons/icon-checked-build-active.svg"

interface Props {
  onSuccess: () => void
}

type Status = "recovering" | "success" | "error"

export const PrivateKeyRecoveryModal = ({ onSuccess }: Props) => {
  const [status, setStatus] = useState<Status>("recovering")
  const [errorCode, setErrorCode] = useState("")
  const copied = useBoolean(false)

  useEffect(() => {
    if (!copied.value) return
    const timer = setTimeout(() => copied.setValue(false), 2000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copied.value])

  useEffect(() => {
    if (status !== "success") return
    const timer = setTimeout(() => onSuccess(), 2000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  // Auto attempt recovery on mount
  useEffect(() => {
    const recover = async () => {
      try {
        const vaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""
        const userId = localStorage.getItem("user_id") || ""

        const { data: keys } = await getEncryptedKeyMiddleware({ type: GetKeysType.PrivateKey })
        if (!keys || keys.length === 0 || !keys[0]?.encrypted_key) {
          throw new Error("No private key from server")
        }

        const privateKeyPem = (await encryptionGCMHelper.decryptContext(
          vaultKey,
          keys[0].encrypted_key
        )) as string

        if (!privateKeyPem || privateKeyPem === keys[0].encrypted_key) {
          throw new Error("Vault key cannot decrypt private key")
        }

        await updateData(Stores.PrivateKeys, { id: userId, privateKey: privateKeyPem })
        setStatus("success")
      } catch {
        const code = generateTRCCode()
        Sentry.captureMessage(`Private key recovery failed - ${code}`, {
          level: "error",
          tags: { error_code: code, error_type: "private_key_recovery_failure" },
          extra: {
            workspace_id: localStorage.getItem("last_organization_db_name") || "",
            breadcrumb: "PageLayout → PrivateKeyRecoveryModal → auto recovery failed",
          },
        })
        setErrorCode(code)
        setStatus("error")
      }
    }
    recover()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCopy = () => {
    navigator.clipboard.writeText(errorCode)
    copied.setValue(true)
  }

  const handleLogout = () => {
    localStorage.clear()
    pushTo(PATH.login)
  }

  const handleTryAgain = () => {
    setStatus("recovering")
    setErrorCode("")
  }

  // Retry on "Try Again"
  useEffect(() => {
    if (status !== "recovering" || !errorCode) return
    // errorCode is set only after a failure, so this effect re-triggers the recovery
    const recover = async () => {
      try {
        const vaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""
        const userId = localStorage.getItem("user_id") || ""

        const { data: keys } = await getEncryptedKeyMiddleware({ type: GetKeysType.PrivateKey })
        if (!keys || keys.length === 0 || !keys[0]?.encrypted_key) {
          throw new Error("No private key from server")
        }

        const privateKeyPem = (await encryptionGCMHelper.decryptContext(
          vaultKey,
          keys[0].encrypted_key
        )) as string

        if (!privateKeyPem || privateKeyPem === keys[0].encrypted_key) {
          throw new Error("Vault key cannot decrypt private key")
        }

        await updateData(Stores.PrivateKeys, { id: userId, privateKey: privateKeyPem })
        setStatus("success")
      } catch {
        const code = generateTRCCode()
        Sentry.captureMessage(`Private key recovery retry failed - ${code}`, {
          level: "error",
          tags: { error_code: code, error_type: "private_key_recovery_failure" },
          extra: {
            workspace_id: localStorage.getItem("last_organization_db_name") || "",
            breadcrumb: "PageLayout → PrivateKeyRecoveryModal → retry failed",
          },
        })
        setErrorCode(code)
        setStatus("error")
      }
    }
    recover()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  if (status === "success") {
    return (
      <ModalCustom
        styleLayout={{ backgroundColor: "#222222" }}
        widthModal={440}
        label="Session Restored"
        styleHeaderModal={{ height: "auto", paddingTop: 16 }}
        bodyChildren={
          <div className="flex flex-col items-center gap-y-3 py-4">
            <IconHasKey width={35} height={35} />
            <p className="text-[15px] font-semibold text-[#222222]">
              Recovery successful!
            </p>
            <p className="text-[13px] text-[#555555] text-center leading-[20px]">
              Your encryption session has been restored.
            </p>
          </div>
        }
      />
    )
  }

  if (status === "error") {
    return (
      <ModalCustom
        widthModal={440}
        styleLayout={{ backgroundColor: "#222222" }}
        label="Encryption Session Error"
        styleHeaderModal={{ height: "auto", paddingTop: 16 }}
        bodyChildren={
          <div className="flex flex-col gap-y-4 pb-2">
            <p className="text-[14px] text-[#222222] font-medium leading-[22px]">
              We were unable to restore your encryption session.
            </p>
            <div className="flex items-center gap-x-2 bg-[#F5F5F5] rounded-md px-3 py-2">
              <span className="text-[14px] font-mono font-semibold text-[#222222] flex-1">
                Error code: {errorCode}
              </span>
              <button
                className="text-[12px] text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                onClick={handleCopy}
              >
                {copied.value ? "Copied!" : "Copy"}
              </button>
            </div>
            <p className="text-[13px] text-[#555555] leading-[20px]">
              Share this code with our support team for faster assistance.
            </p>
            <div className="flex flex-col gap-y-2 mt-2">
              <Button
                widthBtn="100%"
                title="Log out & Try Again"
                colorBtn="yellow"
                onClick={handleLogout}
              />
              <Button
                widthBtn="100%"
                title="Try Again"
                colorBtn="white"
                onClick={handleTryAgain}
              />
            </div>
          </div>
        }
      />
    )
  }

  // "recovering" — spinner while auto-fetching
  return (
    <ModalCustom
      styleLayout={{ backgroundColor: "#222222" }}
      widthModal={440}
      label="Restoring Encryption Session"
      styleHeaderModal={{ height: "auto", paddingTop: 16 }}
      bodyChildren={
        <div className="flex flex-col items-center gap-y-3 py-6">
          <div
            style={{
              width: 32,
              height: 32,
              border: "3px solid #E4E4E4",
              borderTopColor: "#E87A16",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <p className="text-[14px] text-[#555555] text-center leading-[22px]">
            Restoring your encryption keys...
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      }
    />
  )
}
